import { Tooltip } from 'antd';
import classNames from 'classnames';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { Button, ButtonSizes, ButtonVariant, Icon, Typography } from '@/shared/ui';

import { mediaQueriesConfig } from '@/config/mediaQueries';

import { trackDeafSupportButtonClick } from '@/loader/trackDeafSupportButtonClick';

import AuthContext from '@/contexts/Auth/AuthContext';

import './DeafSupportStyles.scss';

interface Props {
  className?: string;
}

export const DeafSupport = React.memo<Props>(function ({ className }) {
  const frameRef = useRef(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const { storedLinks } = useContext(AuthContext);
  const [isVideoOpen, setIsVideoOpen] = useState<boolean>(false);

  const toggleVideo = useCallback(() => {
    const [screenSmMin] = mediaQueriesConfig['screenSM'].split('px');

    if (window.innerWidth >= Number(screenSmMin || '0')) {
      setIsVideoOpen((prevValue) => !prevValue);
    } else if (storedLinks.videoInstruction.deafSupport) {
      window.location.href = storedLinks.videoInstruction.deafSupport;
    }
  }, [storedLinks.videoInstruction.deafSupport]);

  useEffect(() => {
    function onClickOutside(e: MouseEvent): void {
      if (e.target === buttonRef.current || e.target === buttonRef.current?.children[0]) return;
      if (isVideoOpen && e.target !== frameRef.current) setIsVideoOpen(false);
    }

    document.addEventListener('click', onClickOutside, { capture: false });

    return (): void => document.removeEventListener('click', onClickOutside);
  }, [isVideoOpen]);

  if (!storedLinks.videoInstruction.deafSupport) {
    return <React.Fragment />;
  }

  return (
    <>
      <Tooltip
        styles={{
          body: {
            color: 'var(--black)',
            backgroundColor: 'var(--yellow)',
          },
        }}
        classNames={{ body: 'deaf-support' }}
        placement={isVideoOpen ? 'leftBottom' : 'topLeft'}
        title={
          <div className="deaf-support__content">
            <Typography as="p">
              Міністерство внутрішніх справ України <br /> Інструкція жестовою мовою
            </Typography>
          </div>
        }
      >
        <span>
          <Button
            variant={ButtonVariant.ICON}
            size={ButtonSizes.SM}
            innerRef={buttonRef}
            className={classNames('deaf-support__btn', className)}
            onClick={(): void => {
              toggleVideo();
              !isVideoOpen && trackDeafSupportButtonClick();
            }}
          >
            <Icon name="deaf_support" className="deaf-support__icon" />
          </Button>
        </span>
      </Tooltip>

      {isVideoOpen && (
        <iframe
          ref={frameRef}
          className="deaf-support__iframe"
          src={storedLinks.videoInstruction.deafSupport ?? ''}
          title="Як замовити онлайн-послугу на сайті МВС? [жестова мова]"
          allow="autoplay"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      )}
    </>
  );
});
